import { useCallback, useEffect, useRef, useState } from "react";
import { MapMarker } from "../components/map/map.types";

type HoverAddressDetailsProps = {
  allowTooltip?: boolean;
  findAddress: (id: string) => MapMarker | undefined;
};

const SHOW_DELAY_MS = 150;

const useHoverAddressDetails = ({
  allowTooltip = true,
  findAddress,
}: HoverAddressDetailsProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [hoveredAddress, setHoveredAddress] = useState<MapMarker | null>(null);

  // Kept in refs so the handlers below stay stable. The route planner's order
  // boxes are memoized and ignore handler prop changes, so they keep whichever
  // handler they got first. A handler that read state would clear an old timer
  // on mouse leave and let the pending one open the tooltip after the cursor
  // had already left, leaving it stuck open.
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isHoveringBoxRef = useRef(false);
  const allowTooltipRef = useRef(allowTooltip);
  const findAddressRef = useRef(findAddress);
  allowTooltipRef.current = allowTooltip;
  findAddressRef.current = findAddress;

  const clearHoverTimeout = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(
    (locationId: string, event: React.MouseEvent) => {
      if (!allowTooltipRef.current) return;

      const hoverAddress = findAddressRef.current(locationId);
      if (hoverAddress === undefined) return;

      const position = { x: event.clientX, y: event.clientY };

      clearHoverTimeout();
      hoverTimeoutRef.current = setTimeout(() => {
        hoverTimeoutRef.current = null;
        setHoveredAddress(hoverAddress);
        setHoverPosition(position);
        setIsHovering(true);
      }, SHOW_DELAY_MS);
    },
    [clearHoverTimeout],
  );

  const handleMouseLeave = useCallback(() => {
    clearHoverTimeout();
    if (!isHoveringBoxRef.current) {
      setIsHovering(false);
    }
  }, [clearHoverTimeout]);

  const handleBoxMouseEnter = useCallback(() => {
    isHoveringBoxRef.current = true;
  }, []);

  const handleBoxMouseLeave = useCallback(() => {
    isHoveringBoxRef.current = false;
    setIsHovering(false);
  }, []);

  // Hide the tooltip (and drop any pending one) once tooltips are disabled,
  // e.g. when a drag starts.
  useEffect(() => {
    if (!allowTooltip) {
      clearHoverTimeout();
      setIsHovering(false);
    }
  }, [allowTooltip, clearHoverTimeout]);

  useEffect(() => clearHoverTimeout, [clearHoverTimeout]);

  return {
    handleBoxMouseLeave,
    handleBoxMouseEnter,
    handleMouseLeave,
    handleMouseEnter,
    isHovering,
    hoverPosition,
    hoveredAddress,
  };
};

export default useHoverAddressDetails;
